import ParameterValidator from '@mvps-genesis/parameter-validator';
import decodeEntities from '@mvps-genesis/decode-entities';
import MicroModal from 'micromodal';

/*
 *--------------------------------------------------------------------------
 * Modal
 *--------------------------------------------------------------------------
 *
 * Class to enable creation and interaction with modal dialogs.
 *
 * This class currently utlizes the Micromodal.js library. Documentation for
 * the library can be referenced here - https://micromodal.vercel.app/
 */
export default class Modal {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Class options object. See class constructor() for option definitions.
	 *
	 * @var Object
	 */
	options;

	/**
	 * Modal close attribute name
	 *
	 * @var	String
	 */
	closeAttribute;

	/**
	 * Modal trigger attribute name
	 *
	 * @var	String
	 */
	triggerAttribute;

	/**
	 * List of modal trigger elements
	 *
	 * @var	Array
	 */
	triggerElements;

	/**
	 * Modal options attribute name
	 *
	 * @var	String
	 */
	optionsAttribute;

	/**
	 * Determines if a user prefers reduced motion or not.
	 * If they do, `awaitCloseAnimation` and `awaitOpenAnimation` will be set to false.
	 *
	 * @var	Boolean
	 */
	allowsAnimation;

	/**
	 * Create a new Modal object
	 *
	 * @param	Object	options	Micromodal.js configuration object
	 *
	 */
	constructor (options = {
		awaitCloseAnimation: true,
		awaitOpenAnimation: true,
		disableScroll: true,
		openClass: 'is-open',
		triggerAttribute: 'data-micromodal-trigger'
	}) {
		this.#validator = new ParameterValidator(this);
		this.options = this.#validator.isObject('options', options);
		this.closeAttribute = 'data-micromodal-close';
		this.triggerAttribute = this.#validator.isString('options.triggerAttribute', options.triggerAttribute);
		this.triggerElements = [];
		this.optionsAttribute = 'data-modal-options';

		this.allowsAnimation = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

		this.options.awaitCloseAnimation = this.allowsAnimation ? this.options.awaitCloseAnimation : false;
		this.options.awaitOpenAnimation = this.allowsAnimation ? this.options.awaitOpenAnimation : false;
	}

	// eslint-disable-next-line max-lines-per-function
	init (closeEventIds) {
		this.triggerElements = document.querySelectorAll(`[${this.triggerAttribute}]`);

		const triggerElementsLen = this.triggerElements.length;

		if (!triggerElementsLen) {
			return;
		}

		for (let i = 0; i < triggerElementsLen; i++) {
			const element = this.triggerElements[i];

			element.addEventListener('click', (event) => {
				event.preventDefault();
			});

			if (!element.hasAttribute('aria-haspopup')) {
				element.setAttribute('aria-haspopup', 'dialog');
			}

			// Init modals with options set through HTML attribute
			const optionsAttrVal = element.getAttribute(this.optionsAttribute);

			if (optionsAttrVal) {
				let options = JSON.parse(decodeEntities(optionsAttrVal));
				const triggerValue = element.getAttribute(this.triggerAttribute);
				const newTriggerValue = `${this.triggerAttribute}-${i}`;

				element.removeAttribute(this.triggerAttribute);
				element.setAttribute(newTriggerValue, triggerValue);

				options.openTrigger = newTriggerValue;

				options.awaitCloseAnimation = this.allowsAnimation ? options.awaitCloseAnimation : false;
				options.awaitOpenAnimation = this.allowsAnimation ? options.awaitOpenAnimation : false;

				MicroModal.init(options);
			}
		}

		// Init modals without options set through HTML attribute
		MicroModal.init(this.options);

		// Add close events if passed in
		if (!Array.isArray(closeEventIds) || !closeEventIds.length) {
			return;
		}

		for (const id of closeEventIds) {
			const closeEventElement = document.getElementById(id);

			if (!closeEventElement) {
				return;
			}

			this.addCloseEvent(closeEventElement);
		}
	}

	addCloseEvent (element) {
		if (!element) {
			return;
		}

		document.addEventListener('mousedown', (event) => {
			event.stopPropagation();

			if (element === event.target || element.contains(event.target)) {
				return;
			}

			let closeModal = true;

			for (const trigger of this.triggerElements) {
				if (trigger === event.target || trigger.contains(event.target)) {
					closeModal = false;
					break;
				}
			}

			if (!closeModal || !element.classList.contains(this.options.openClass || 'is-open')) {
				return;
			}

			const closeElement = element.querySelector(`[${this.closeAttribute}]`);

			if (closeElement) {
				closeElement.click();
			}
		});
	}
}
